import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { Plus, Trash2, Check, Loader } from 'lucide-react';

export default function GoalsView({ user, onYearChange, onMonthChange }) {
  const [yearlyGoals, setYearlyGoals] = useState([]);
  const [monthlyGoals, setMonthlyGoals] = useState([]);
  const [newYearlyGoal, setNewYearlyGoal] = useState('');
  const [newMonthlyGoal, setNewMonthlyGoal] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [addingYearly, setAddingYearly] = useState(false);
  const [addingMonthly, setAddingMonthly] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    loadGoals();
  }, [user, selectedYear, selectedMonth]);

  const loadGoals = async () => {
    setLoading(true);
    try {
      const yearlyQ = query(
        collection(db, 'goals'),
        where('userId', '==', user.uid),
        where('type', '==', 'yearly'),
        where('year', '==', selectedYear)
      );
      const yearlySnapshot = await getDocs(yearlyQ);
      setYearlyGoals(yearlySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const monthlyQ = query(
        collection(db, 'goals'),
        where('userId', '==', user.uid),
        where('type', '==', 'monthly'),
        where('year', '==', selectedYear),
        where('month', '==', selectedMonth)
      );
      const monthlySnapshot = await getDocs(monthlyQ);
      setMonthlyGoals(monthlySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const addYearlyGoal = async () => {
    if (!newYearlyGoal.trim()) return;
    setAddingYearly(true);
    try {
      await addDoc(collection(db, 'goals'), {
        userId: user.uid,
        type: 'yearly',
        title: newYearlyGoal,
        year: selectedYear,
        completed: false,
        createdAt: serverTimestamp(),
      });
      setNewYearlyGoal('');
      loadGoals();
    } catch (error) {
      console.error('Error adding yearly goal:', error);
    } finally {
      setAddingYearly(false);
    }
  };

  const addMonthlyGoal = async () => {
    if (!newMonthlyGoal.trim()) return;
    setAddingMonthly(true);
    try {
      await addDoc(collection(db, 'goals'), {
        userId: user.uid,
        type: 'monthly',
        title: newMonthlyGoal,
        year: selectedYear,
        month: selectedMonth,
        completed: false,
        createdAt: serverTimestamp(),
      });
      setNewMonthlyGoal('');
      loadGoals();
    } catch (error) {
      console.error('Error adding monthly goal:', error);
    } finally {
      setAddingMonthly(false);
    }
  };

  const deleteGoal = async (id) => {
    setProcessingId(id);
    try {
      await deleteDoc(doc(db, 'goals', id));
      loadGoals();
    } catch (error) {
      console.error('Error deleting goal:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const toggleGoalComplete = async (id, completed) => {
    setProcessingId(id);
    try {
      await updateDoc(doc(db, 'goals', id), { completed: !completed });
      loadGoals();
    } catch (error) {
      console.error('Error updating goal:', error);
    } finally {
      setProcessingId(null);
    }
  };

  const monthNames = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];

  const GoalsList = ({ goals, onToggle, onDelete, accentVar }) => (
    <div className="space-y-2">
      {goals.length === 0 ? (
        <p className="text-sm text-slate-400 italic py-3">No goals yet</p>
      ) : (
        goals.map(goal => (
          <div key={goal.id} className="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-lg hover:shadow-md transition-all">
            <button
              onClick={() => onToggle(goal.id, goal.completed)}
              disabled={processingId === goal.id}
              className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                processingId === goal.id ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={goal.completed
                ? { backgroundColor: 'var(--color-secondary)', borderColor: 'var(--color-secondary)' }
                : { borderColor: '#cbd5e1' }}
            >
              {processingId === goal.id ? (
                <Loader size={12} className="animate-spin" />
              ) : goal.completed ? (
                <Check size={14} className="text-white" />
              ) : null}
            </button>

            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${goal.completed ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                {goal.title}
              </p>
            </div>

            <button
              onClick={() => onDelete(goal.id)}
              disabled={processingId === goal.id}
              className={`p-1 text-slate-400 hover:text-red-500 transition-colors flex-shrink-0 ${processingId === goal.id ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {processingId === goal.id
                ? <Loader size={16} className="animate-spin text-red-500" />
                : <Trash2 size={16} />}
            </button>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Yearly Goals */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h2 className="text-2xl font-bold text-slate-800 mb-4">Yearly Goals</h2>

        <div className="mb-6 flex gap-2">
          <select
            value={selectedYear}
            onChange={(e) => { const y = parseInt(e.target.value); setSelectedYear(y); onYearChange?.(y); }}
            disabled={loading}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none disabled:opacity-50"
          >
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={newYearlyGoal}
              onChange={(e) => setNewYearlyGoal(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addYearlyGoal()}
              disabled={addingYearly}
              placeholder="Add a yearly goal..."
              className="flex-1 px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none disabled:bg-slate-100 disabled:cursor-not-allowed"
            />
            <button
              onClick={addYearlyGoal}
              disabled={addingYearly}
              style={{ backgroundColor: 'var(--color-primary)' }}
              className="p-2 text-white rounded-lg transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addingYearly ? <Loader size={20} className="animate-spin" /> : <Plus size={20} />}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-6">
            <Loader size={24} className="mx-auto animate-spin" style={{ color: 'var(--color-primary)' }} />
            <p className="text-sm text-slate-600 mt-2">Loading...</p>
          </div>
        ) : (
          <GoalsList goals={yearlyGoals} onToggle={toggleGoalComplete} onDelete={deleteGoal} />
        )}
      </div>

      {/* Monthly Goals */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h2 className="text-2xl font-bold text-slate-800 mb-4">Monthly Goals</h2>

        <div className="mb-6 flex gap-2">
          <select
            value={selectedMonth}
            onChange={(e) => { const m = parseInt(e.target.value); setSelectedMonth(m); onMonthChange?.(m); }}
            disabled={loading}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none disabled:opacity-50"
          >
            {monthNames.map((month, index) => (
              <option key={index + 1} value={index + 1}>{month}</option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => { const y = parseInt(e.target.value); setSelectedYear(y); onYearChange?.(y); }}
            disabled={loading}
            className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none disabled:opacity-50"
          >
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={newMonthlyGoal}
              onChange={(e) => setNewMonthlyGoal(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addMonthlyGoal()}
              disabled={addingMonthly}
              placeholder="Add a monthly goal..."
              className="flex-1 px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none disabled:bg-slate-100 disabled:cursor-not-allowed"
            />
            <button
              onClick={addMonthlyGoal}
              disabled={addingMonthly}
              style={{ backgroundColor: 'var(--color-secondary)' }}
              className="p-2 text-white rounded-lg transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addingMonthly ? <Loader size={20} className="animate-spin" /> : <Plus size={20} />}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-6">
            <Loader size={24} className="mx-auto animate-spin" style={{ color: 'var(--color-secondary)' }} />
            <p className="text-sm text-slate-600 mt-2">Loading...</p>
          </div>
        ) : (
          <GoalsList goals={monthlyGoals} onToggle={toggleGoalComplete} onDelete={deleteGoal} />
        )}
      </div>
    </div>
  );
}
